/* eslint-disable @typescript-eslint/ban-ts-comment */
// @ts-nocheck

import { gsap } from 'gsap';
import {
    AdditiveBlending,
    Color,
    Mesh,
    NormalBlending,
    Object3D,
    PlaneGeometry,
    ShaderMaterial,
    Vector2,
    // MultiplyBlending,
    // NoBlending,
    // SubtractiveBlending,
} from 'three';

import Loaders from '../../../../utils/Loaders';
import PerformanceProfile from '../../../../utils/PerformanceProfile';

import { fragmentShader, vertexShader } from './shaders';

const GLOBAL_CONFIG = {
    alpha: 0.75,
    blending: 1,
};

const blendings = [
    { text: 'Normal', value: NormalBlending },
    { text: 'Additive', value: AdditiveBlending },
    // { text: 'Subscractive', value: SubtractiveBlending },
    // { text: 'Multiply', value: MultiplyBlending }
];

const CONFIG = [
    {
        name: 'explosion',
        ratio: 1200 / 1080,
        column: 5,
        row: 5,
        frames: 26,
        frameRate: 30,
        height: 1.0,
        url: '/assets/webgl/textures/sparkles-spritesheet-0.jpg',
        texture: null,
    },
    {
        name: 'ribbon',
        ratio: 1050 / 1080,
        column: 7,
        row: 6,
        frames: 37,
        frameRate: 20,
        height: 0.85,
        url: '/assets/webgl/textures/sparkles-spritesheet-1.jpg',
        texture: null,
    },
    {
        name: 'stars',
        ratio: 1360 / 1080,
        column: 4,
        row: 5,
        frames: 17,
        frameRate: 20,
        height: 0.65,
        url: '/assets/webgl/textures/sparkles-spritesheet-2.jpg',
        texture: null,
    },
];

const TIER_CONFIG = [
    {
        color: '#ffffff',
    },
    {
        color: '#ffffff',
    },
    {
        color: '#ffffff',
    },
    {
        color: '#ffffff',
    },
];

let GUI;

const addGUI = async () => {
    await PerformanceProfile.devModeReady();

    GUI = PerformanceProfile.GUI.floorGameUp.addFolder({
        title: 'Sparkles',
        expanded: false,
    });

    GUI.addButton({ title: 'Export Config' }).on('click', () => {
        PerformanceProfile.exportConfig('Sparkles', { ...GLOBAL_CONFIG, ...CONFIG });
    });

    GUI.addInput(GLOBAL_CONFIG, 'alpha', { min: 0, max: 1.0, step: 0.001 });

    GUI.addBlade({
        view: 'list',
        label: 'blending',
        options: blendings,
        value: blendings[GLOBAL_CONFIG.blending].value,
    }).on('change', (blade) => {
        GLOBAL_CONFIG.blending = blade.value;
    });

    for (let i = 0; i < CONFIG.length; i++) {
        const folder = GUI.addFolder({
            title: CONFIG[i].name,
            expanded: false,
        });

        folder.addInput(CONFIG[i], 'height', { min: 0, max: 1.5, step: 0.001 });
        folder.addInput(CONFIG[i], 'frameRate', { min: 0, max: 60, step: 1 });
    }

    for (let i = 0; i < TIER_CONFIG.length; i++) {
        const folder = GUI.addFolder({
            title: `Tier ${i + 1}`,
            expanded: false,
        });

        folder.addInput(TIER_CONFIG[i], 'color');
    }
};

if (PerformanceProfile.devMode) addGUI();

interface SparklesOptions {
    type: number;
    tier: number;
    // TODO: fix type
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    camera: any;
}

export default class Sparkles extends Object3D {
    tier: number;
    mesh: Mesh;
    // TODO: What is this type?
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    camera: any;

    visible = false;

    constructor({ type, tier, camera }: SparklesOptions) {
        super();

        // TODO: `Object3D` only accepts `string` for `type`.
        this.type = type || 0;

        this.tier = tier;
        this.camera = camera;

        const geometry = new PlaneGeometry(1.0, 1.0);
        const material = new ShaderMaterial({
            uniforms: {
                uTexture: { value: null },
                uSpriteSize: { value: new Vector2(1.0, 1.0) },
                uSpriteIndex: { value: new Vector2(0.0, 0.0) },
                uTransition: { value: 0.0 },
                uAlpha: { value: GLOBAL_CONFIG.alpha },
                uColor: { value: new Color(TIER_CONFIG[this.tier].color) },
            },
            vertexShader,
            fragmentShader,
            transparent: true,
        });
        material.blending = blendings[GLOBAL_CONFIG.blending].value;

        this.mesh = new Mesh(geometry, material);
        this.add(this.mesh);

        this.mesh.scale.set(CONFIG[this.type].height * CONFIG[this.type].ratio, CONFIG[this.type].height, 1.0);

        this.mesh.material.uniforms.uSpriteSize.value.x = 1.0 / CONFIG[this.type].row;
        this.mesh.material.uniforms.uSpriteSize.value.y = 1.0 / CONFIG[this.type].column;

        this.loadTexture();

        if (PerformanceProfile.devMode) {
            GUI.on('change', this.updateUniforms.bind(this));
        }
    }

    loadTexture() {
        // THREE.NearestFilter
        // THREE.LinearFilter
        if (CONFIG[this.type].texture) {
            this.mesh.material.uniforms.uTexture.value = CONFIG[this.type].texture;
            this.mesh.material.uniforms.uTexture.value.needsUpdate = true;
        } else {
            Loaders.textureLoader.load(CONFIG[this.type].url, (texture) => {
                CONFIG[this.type].texture = texture;
                this.mesh.material.uniforms.uTexture.value = texture;
                this.mesh.material.uniforms.uTexture.value.needsUpdate = true;
            });
        }
    }

    animateIn(delay: number, withScale = false) {
        setTimeout(() => {
            this.playSprite();

            setTimeout(
                () => {
                    this.visible = false;
                },
                (1000 / CONFIG[this.type].frameRate) * (CONFIG[this.type].frames + 1.0),
            );
        }, delay * 1000);

        if (withScale) {
            gsap.fromTo(
                this.scale,
                {
                    x: 0.7,
                    y: 0.7,
                },
                {
                    x: 1.0,
                    y: 1.0,
                    ease: 'expo.out',
                    duration: 2.2,
                    delay: delay - 0.2,
                    onStart: () => {
                        this.visible = true;
                    },
                },
            );
        }
    }

    playSprite() {
        this.visible = true;

        let idX = 0.0;
        let idY = 0.0;

        for (let i = 0; i < CONFIG[this.type].frames; i++) {
            idX = i % CONFIG[this.type].column;

            const x = idX / CONFIG[this.type].column;
            const y = idY / CONFIG[this.type].row;

            setTimeout(
                () => {
                    this.mesh.material.uniforms.uSpriteIndex.value.x = x;
                    this.mesh.material.uniforms.uSpriteIndex.value.y = y;
                },
                (1000 / CONFIG[this.type].frameRate) * i,
            );

            if (idX === CONFIG[this.type].column - 1) idY += 1.0;
            idX += 1.0;
        }
    }

    updateUniforms() {
        this.mesh.material.blending = blendings[GLOBAL_CONFIG.blending].value;
        this.mesh.material.uniforms.uAlpha.value = GLOBAL_CONFIG.alpha;
        this.mesh.material.uniforms.uColor.value.setStyle(TIER_CONFIG[this.tier].color);
        this.mesh.scale.set(CONFIG[this.type].height * CONFIG[this.type].ratio, CONFIG[this.type].height, 1.0);
    }

    update() {
        if (!this.visible) return;

        this.quaternion.copy(this.camera.quaternion);
    }

    delete() {
        if (this.mesh.material.uniforms.uTexture.value) {
            this.mesh.material.uniforms.uTexture.value.dispose();
        }

        this.mesh.material.dispose();
        this.mesh.geometry.dispose();
    }
}
